/**
 * A tela de cadastro. Uma, para todas as entidades.
 *
 * Não é economia de código — é a decisão de produto. Vinte e duas telas
 * escritas à mão divergem em três meses: uma ganha um botão que a outra não
 * tem, outra chama "excluir" de "remover", e daí para as 179 telas do
 * concorrente é só questão de tempo. Uma tela dirigida por metadado não
 * diverge porque não tem onde.
 *
 * O que a tela faz de específico por entidade vem de `campos.ts`: rótulo em
 * português, ajuda curta e, principalmente, a SEÇÃO — que é como os
 * dezessete campos do professor cabem numa tela em vez de quatro.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'

import { ErroApi, api, type Linha, type Recurso } from '../api'
import { camposDe, secoesDe, type Campo, type TipoCampo } from '../campos'
import { Icon } from '../components/Icon'
import { Painel } from '../components/Painel'
import { Horarios } from './Horarios'
import { Relacoes } from './Relacoes'
import { rotuloDe, rotulosDistintos } from '../rotulos'

type Estado = 'carregando' | 'pronto' | 'erro'

/** Como chamar uma linha quando só se tem o id dela. */
/** A semana útil, pela numeração de `DiaSemana`. Sábado e domingo não entram
 *  porque nenhuma escola do piloto tem aula neles — e a lista curta é o que
 *  faz as caixas caberem numa linha. */
const DIAS_DA_SEMANA: Array<[number, string]> = [
  [1, 'Seg'],
  [2, 'Ter'],
  [3, 'Qua'],
  [4, 'Qui'],
  [5, 'Sex'],
]


/**
 * As opções de cada campo de referência, buscadas uma vez por recurso.
 *
 * **Por que isto não existia, e por que precisava existir.** Toda chave
 * estrangeira era caixa de texto: cadastrar uma turma significava colar o
 * UUID do curso. Escrever o manual foi o que tornou isso impossível de
 * ignorar — não há como explicar "escolha o curso" numa tela onde não se
 * escolhe nada.
 *
 * Busca em paralelo e tolera falha individual: um recurso que a escola
 * escondeu ou que ainda não tem nada cadastrado devolve lista vazia, e o
 * campo mostra isso em vez de quebrar a tela inteira.
 */
function useReferencias(projeto: string, campos: Campo[]) {
  const [mapa, setMapa] = useState<Record<string, Linha[]>>({})

  // O conjunto de recursos referenciados muda só quando a tela muda, mas o
  // array é novo a cada render — a chave estável evita buscar em laço.
  const alvos = [...new Set(campos.map((c) => c.referencia).filter((r): r is string => !!r))]
  const chave = alvos.sort().join(',')

  useEffect(() => {
    let vivo = true
    const quais = chave ? chave.split(',') : []
    void Promise.all(
      quais.map(async (caminho) => [caminho, await api.listar(projeto, caminho).catch(() => [])] as const),
    ).then((pares) => {
      if (vivo) setMapa(Object.fromEntries(pares))
    })
    return () => {
      vivo = false
    }
  }, [projeto, chave])

  return mapa
}

export function Cadastro({
  projeto,
  recurso,
  recursos,
  abrirFormulario,
}: {
  projeto: string
  recurso: Recurso
  /** O catálogo inteiro — as relações precisam dele para traduzir tabela em tela. */
  recursos: Recurso[]
  /**
   * Abre um registro assim que a tela monta.
   *
   * Existe por causa do tour: o manual aponta para os campos, e os campos só
   * existem quando alguém está editando. Sem isto, o tour chegaria numa lista
   * e diria "esta parte da tela mudou" sobre uma tela que está inteira.
   *
   * Abre o PRIMEIRO EXISTENTE quando há algum, e não um em branco. Duas
   * razões: o tour passa a apontar para dados reais da própria escola, que é
   * a vantagem inteira de apontar em vez de mostrar print; e horários e
   * relações só aparecem num registro que já existe, porque apontam para o id
   * dele.
   */
  abrirFormulario?: boolean
}) {
  const [itens, setItens] = useState<Linha[]>([])
  const [estado, setEstado] = useState<Estado>('carregando')
  const [erro, setErro] = useState<string | null>(null)
  const [editando, setEditando] = useState<Linha | null>(null)
  const [busca, setBusca] = useState('')

  const campos = camposDe(recurso.caminho, recurso.campos)
  const opcoesDe = useReferencias(projeto, campos)

  /**
   * Procurar numa lista de 312 linhas.
   *
   * **Procura no que a pessoa vê, e não no que o banco guarda.** Quem digita
   * "AD1A" numa lista de ofertas está procurando a turma, e a turma chega na
   * linha como `turma_id`, um UUID. Por isso a busca resolve a referência
   * para o nome ANTES de comparar — sem isso, procurar pelo que está escrito
   * na tela não acha nada, que é a pior forma de um filtro falhar.
   *
   * Sem acento e sem caixa: ninguém digita "História" com o acento quando
   * está com pressa, e uma busca que exige acento é uma busca que não acha.
   */
  const semAcento = (s: string) =>
    s.normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase()

  const nomePorId = useMemo(() => {
    const m = new Map<string, string>()
    for (const linhas of Object.values(opcoesDe)) {
      for (const [id, rotulo] of rotulosDistintos(linhas)) m.set(id, rotulo)
    }
    return m
  }, [opcoesDe])

  const filtrados = useMemo(() => {
    const alvo = semAcento(busca.trim())
    if (!alvo) return itens
    return itens.filter((item) =>
      campos.some((c) => {
        const bruto = item[c.nome]
        if (bruto === null || bruto === undefined) return false
        const texto =
          c.tipo === 'referencia' ? (nomePorId.get(String(bruto)) ?? String(bruto)) : String(bruto)
        return semAcento(texto).includes(alvo)
      }),
    )
  }, [itens, busca, campos, nomePorId])

  const recarregar = useCallback(async () => {
    setEstado('carregando')
    try {
      const linhas = await api.listar(projeto, recurso.caminho)
      setItens(linhas)
      setEstado('pronto')
      setErro(null)
      // O tour abre o primeiro registro assim que a lista chega. Fica aqui,
      // e não num efeito à parte, porque "o primeiro" só existe depois da
      // resposta — e porque um efeito disparado por `itens` reabriria o
      // formulário toda vez que a lista fosse recarregada.
      if (abrirFormulario) setEditando(linhas[0] ?? {})
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'não foi possível carregar')
      setEstado('erro')
    }
  }, [projeto, recurso.caminho, abrirFormulario])

  useEffect(() => {
    void recarregar()
  }, [recarregar])

  async function salvar(dados: Linha) {
    const id = editando?.id as string | undefined
    if (id) await api.atualizar(projeto, recurso.caminho, id, dados)
    else await api.criar(projeto, recurso.caminho, dados)
    setEditando(null)
    await recarregar()
  }

  async function excluir(id: string) {
    await api.excluir(projeto, recurso.caminho, id)
    await recarregar()
  }

  return (
    <>
      <header className="page-head">
        <h1>{recurso.rotulo}</h1>
        <p className="page-head__note">
          {estado === 'pronto' ? `${itens.length} cadastrado${itens.length === 1 ? '' : 's'}` : ''}
        </p>
      </header>

      {erro && (
        <p className="state state--pendencia">
          <Icon name="aviso" size={14} />
          {erro}
        </p>
      )}

      {/* O botão de criar não some mais quando há algo aberto: o formulário
          saiu da página e foi para o painel, então a lista continua inteira
          atrás dele. */}
      <div className="row">
        <button type="button" className="btn btn--primary" onClick={() => setEditando({})}>
          Novo {recurso.entidade.toLowerCase()}
        </button>
      </div>

      {editando && (
        <Painel
          titulo={
            editando.id
              ? `${recurso.entidade}: ${rotuloDe(editando)}`
              : `Novo ${recurso.entidade.toLowerCase()}`
          }
          nota={editando.id ? undefined : 'Preencha e salve para o registro passar a existir.'}
          aoFechar={() => setEditando(null)}
        >
          <Formulario
            campos={campos}
            valores={editando}
            entidade={recurso.entidade}
            opcoesDe={opcoesDe}
            onCancelar={() => setEditando(null)}
            onSalvar={salvar}
          />

          {/* Relação e horário só existem depois de o registro existir: ambos
              apontam para o id dele. Por isso aparecem ao EDITAR, e não ao
              criar — e é também o que ensina a ordem certa sem explicá-la. */}
          {!!editando.id && (
            <>
              {recurso.caminho === 'esquemas' && (
                <Horarios projeto={projeto} esquema={String(editando.id)} />
              )}
              <Relacoes
                projeto={projeto}
                recurso={recurso}
                registroId={String(editando.id)}
                recursos={recursos}
              />
            </>
          )}
        </Painel>
      )}

      {estado === 'pronto' && itens.length === 0 && (
        <div className="empty">
          <p className="empty__title">Nada cadastrado ainda.</p>
          <p className="empty__body">
            Três caminhos levam dados para dentro: a planilha modelo, um export do URÂNIA, ou
            cadastro à mão. Se a escola já usa outro sistema, começar pelo export poupa a
            redigitação inteira.
          </p>
        </div>
      )}

      {/* **A caixa só aparece a partir de oito linhas.** Em Turnos, que tem
          três, um campo de busca seria mobília: ocupa espaço, pede atenção e
          não resolve problema nenhum. O número é o ponto em que a lista
          deixa de caber de uma olhada. */}
      {itens.length >= 8 && (
        <div className="filtro">
          <label className="field filtro__campo">
            <span className="sr-only">Procurar em {recurso.rotulo.toLowerCase()}</span>
            <input
              className="input"
              type="search"
              value={busca}
              placeholder={`Procurar em ${itens.length} ${recurso.rotulo.toLowerCase()}…`}
              onChange={(e) => setBusca(e.target.value)}
            />
          </label>
          {busca.trim() && (
            <span className="filtro__conta">
              {filtrados.length} de {itens.length}
            </span>
          )}
        </div>
      )}

      {itens.length > 0 && filtrados.length === 0 && (
        <p className="field__hint">
          Nada com “{busca.trim()}”. A busca olha todos os campos do registro, sem acento e sem
          diferenciar maiúscula — inclusive o nome do que está referenciado, não o código dele.
        </p>
      )}

      {filtrados.length > 0 && (
        <Tabela
          campos={campos}
          itens={filtrados}
          opcoesDe={opcoesDe}
          onEditar={setEditando}
          onExcluir={excluir}
        />
      )}
    </>
  )
}

/** Lista sobre réguas — não cartões. */
function Tabela({
  campos,
  itens,
  opcoesDe,
  onEditar,
  onExcluir,
}: {
  campos: Campo[]
  itens: Linha[]
  opcoesDe: Record<string, Linha[]>
  onEditar: (l: Linha) => void
  onExcluir: (id: string) => void
}) {
  /**
   * id → nome, para cada recurso referenciado.
   *
   * Sem isto a lista da grade curricular mostrava três colunas de UUID, e
   * achar uma linha nela era impossível — o que também tornava a tela
   * impossível de documentar: não há como explicar "ache a turma AD1A" onde
   * se lê `01448c60-98e8-471b…`.
   */
  const nomePorId = new Map<string, string>()
  for (const linhas of Object.values(opcoesDe)) {
    for (const [id, rotulo] of rotulosDistintos(linhas)) nomePorId.set(id, rotulo)
  }
  // Só as primeiras colunas na lista: a tabela é para achar, e o formulário
  // para ler tudo. Uma tabela com dezessete colunas não se lê.
  const visiveis = campos.filter((c) => !c.pessoal && c.tipo !== 'derivado').slice(0, 4)

  return (
    <div className="tabela-rolavel">
      <table className="tabela">
        <thead>
          <tr>
            {visiveis.map((c) => (
              <th key={c.nome}>{c.rotulo}</th>
            ))}
            <th className="tabela__acoes">
              <span className="sr-only">Ações</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {itens.map((item) => (
            <tr key={String(item.id)}>
              {visiveis.map((c) => (
                <td
                  key={c.nome}
                  className={c.tipo === 'numero' || c.tipo === 'hora' ? 'mono' : undefined}
                >
                  {c.tipo === 'referencia'
                    ? (nomePorId.get(String(item[c.nome])) ?? formatar(item[c.nome]))
                    : c.tipo === 'escolha' && c.opcoes
                      ? // O rótulo da opção, não o valor do banco: "Terça", e não "2".
                        (c.opcoes.find((o) => o.valor === String(item[c.nome]))?.rotulo ?? formatar(item[c.nome]))
                      : // O formulário já convertia minuto do dia em HH:MM; a
                      // tabela não, e por isso o turno da manhã aparecia como
                      // "450 às 780". O dado estava certo e ilegível.
                      formatar(item[c.nome], c.tipo)}
                </td>
              ))}
              <td className="tabela__acoes">
                {/* Linha do sistema não se edita: os 29 Tipos são o catálogo
                    que vem com o produto, e mexer neles por uma escola os
                    mudaria para todas. A API já recusa; a tela não oferece,
                    que é melhor que oferecer e falhar. */}
                {item.projeto_id === null ? (
                  <span className="field__hint">padrão do sistema</span>
                ) : (
                  <>
                <button type="button" className="btn btn--ghost" onClick={() => onEditar(item)}>
                  Editar
                </button>
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => onExcluir(String(item.id))}
                >
                  Excluir
                </button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** `450` -> `"07:30"`. O banco guarda minuto do dia; a pessoa le hora. */
function paraHora(minutos: number): string {
  const m = ((minutos % 1440) + 1440) % 1440
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
}

/** O inverso. */
function deHora(hora: string): number {
  const [h, m] = hora.split(':')
  return Number(h) * 60 + Number(m)
}

function formatar(valor: unknown, tipo?: TipoCampo): string {
  if (valor === null || valor === undefined || valor === '') return '—'
  if (typeof valor === 'boolean') return valor ? 'sim' : 'não'
  if (tipo === 'hora' && typeof valor === 'number') return paraHora(valor)
  if (tipo === 'moeda' && valor !== null) {
    const n = Number(valor)
    if (Number.isFinite(n)) {
      return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    }
  }
  // Lista de dias: `[1,4]` vira `seg, qui`. Vazio já caiu no traço acima, e
  // ali ele quer dizer "qualquer dia" — que é o que o formulário explica.
  if (tipo === 'dias' && Array.isArray(valor)) {
    return valor.map((d) => ABREVIACAO[Number(d)] ?? String(d)).join(', ')
  }
  return String(valor)
}

const ABREVIACAO: Record<number, string> = {
  1: 'seg',
  2: 'ter',
  3: 'qua',
  4: 'qui',
  5: 'sex',
  6: 'sáb',
  7: 'dom',
}

function Formulario({
  campos,
  valores,
  entidade,
  opcoesDe,
  onCancelar,
  onSalvar,
}: {
  campos: Campo[]
  valores: Linha
  entidade: string
  /** As linhas de cada recurso referenciado, por caminho. */
  opcoesDe: Record<string, Linha[]>
  onCancelar: () => void
  onSalvar: (dados: Linha) => Promise<void>
}) {
  const [rascunho, setRascunho] = useState<Linha>(valores)
  const [erro, setErro] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)

  async function enviar(evento: React.FormEvent) {
    evento.preventDefault()
    setSalvando(true)
    setErro(null)
    try {
      await onSalvar(rascunho)
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'não foi possível salvar')
    } finally {
      setSalvando(false)
    }
  }

  return (
    <form className="form" onSubmit={enviar}>
      {secoesDe(campos).map((secao) => (
        <section className="section" key={secao.nome}>
          <div className="section__head">
            <h2>{secao.nome}</h2>
          </div>
          <div className="form__grade">
            {secao.campos.map((campo) => (
              <CampoForm
                key={campo.nome}
                campo={campo}
                opcoes={campo.referencia ? (opcoesDe[campo.referencia] ?? []) : []}
                valor={rascunho[campo.nome]}
                rascunho={rascunho}
                onMudar={(v) => setRascunho((r) => ({ ...r, [campo.nome]: v }))}
              />
            ))}
          </div>
        </section>
      ))}

      {erro && (
        <p className="field__error">
          <Icon name="aviso" size={13} />
          {erro}
        </p>
      )}

      <div className="row">
        <button type="submit" className="btn btn--primary" disabled={salvando}>
          {salvando ? 'Salvando…' : `Salvar ${entidade.toLowerCase()}`}
        </button>
        <button type="button" className="btn btn--ghost" onClick={onCancelar}>
          Cancelar
        </button>
      </div>
    </form>
  )
}

function CampoForm({
  campo,
  valor,
  rascunho,
  opcoes,
  onMudar,
}: {
  campo: Campo
  valor: unknown
  /** O registro inteiro como está no formulário — o campo derivado lê dele. */
  rascunho: Linha
  /** As linhas do recurso referenciado, quando o campo é uma referência. */
  opcoes: Linha[]
  onMudar: (v: unknown) => void
}) {
  const id = `campo-${campo.nome}`
  const texto = valor === null || valor === undefined ? '' : String(valor)
  const rotulos = useMemo(() => rotulosDistintos(opcoes), [opcoes])

  return (
    // `data-campo` é o que o tour procura. Fica no bloco inteiro, e não no
    // input, porque o que a pessoa precisa ver destacado é o campo com o
    // rótulo e a dica — um retângulo em volta da caixa de texto sozinha não
    // diz de que campo se trata.
    <div className="field" data-campo={campo.nome}>
      <label className="field__label" htmlFor={id}>
        {campo.rotulo}
        {campo.pessoal && (
          <span className="field__pii" title="Dado pessoal (LGPD)">
            pessoal
          </span>
        )}
      </label>

      {campo.tipo === 'derivado' && campo.derivado ? (
        // Só leitura, e muda junto com o campo de que depende: trocar a
        // classificação aqui já mostra o novo valor antes de salvar.
        (() => {
          const d = campo.derivado
          const origem = opcoes.find((o) => String(o.id) === String(rascunho[d.de] ?? ''))
          return (
            <output className="field__derivado mono" id={id}>
              {origem
                ? `${formatar(origem[d.valor], d.formato)} · ${rotuloDe(origem)}`
                : 'escolha a classificação'}
            </output>
          )
        })()
      ) : campo.tipo === 'referencia' ? (
        // Lista, e não UUID. O vazio continua sendo uma escolha legítima:
        // sala padrão, trilha e forma aceitável são todas opcionais, e um
        // campo que obriga a escolher mentiria sobre o modelo.
        <select
          className="select"
          id={id}
          value={texto}
          onChange={(e) => onMudar(e.target.value || null)}
        >
          <option value="">—</option>
          {opcoes.map((o) => (
            <option key={String(o.id)} value={String(o.id)}>
              {rotulos.get(String(o.id)) ?? rotuloDe(o)}
            </option>
          ))}
        </select>
      ) : campo.tipo === 'escolha' ? (
        <select
          className="select"
          id={id}
          value={texto}
          onChange={(e) => onMudar(e.target.value || null)}
        >
          <option value="">—</option>
          {campo.opcoes?.map((o) => (
            <option key={o.valor} value={o.valor}>
              {o.rotulo}
            </option>
          ))}
        </select>
      ) : campo.tipo === 'moeda' ? (
        // `step` de um centavo e `R$` à vista. O banco guarda `numeric`, não
        // float, pela mesma razão que o cálculo soma centavos inteiros: o
        // primeiro uso deste número é conferi-lo contra a folha.
        <div className="input-group">
          <span className="input-group__prefixo">R$</span>
          <input
            className="input input--mono"
            id={id}
            type="number"
            step="0.01"
            min="0"
            value={typeof valor === 'number' || typeof valor === 'string' ? String(valor) : ''}
            onChange={(e) => onMudar(e.target.value === '' ? null : Number(e.target.value))}
          />
        </div>
      ) : campo.tipo === 'dias' ? (
        // Cinco caixas e não um seletor múltiplo: a lista é curta, fixa, e
        // quem monta horário pensa em "segunda e quinta", não em rolar uma
        // lista. **Nenhuma marcada significa qualquer dia**, e é por isso que
        // o rodapé diz isso em voz alta — vazio aqui nunca é "nenhum dia".
        <div className="dias">
          {DIAS_DA_SEMANA.map(([numero, nome]) => {
            const marcados = Array.isArray(valor) ? valor.map(Number) : []
            const tem = marcados.includes(numero)
            return (
              <label key={numero} className="checkbox">
                <input
                  type="checkbox"
                  checked={tem}
                  onChange={() => {
                    const novo = tem
                      ? marcados.filter((d) => d !== numero)
                      : [...marcados, numero].sort((a, b) => a - b)
                    onMudar(novo.length === 0 ? null : novo)
                  }}
                />
                <span>{nome}</span>
              </label>
            )
          })}
        </div>
      ) : campo.tipo === 'booleano' ? (
        <label className="checkbox">
          <input
            type="checkbox"
            id={id}
            checked={valor === true}
            onChange={(e) => onMudar(e.target.checked)}
          />
          <span>{valor === true ? 'Sim' : 'Não'}</span>
        </label>
      ) : campo.tipo === 'hora' ? (
        // O banco guarda minuto do dia; a pessoa digita e lê HH:MM. A
        // conversão fica aqui e em nenhum outro lugar da interface.
        <input
          className="input input--mono"
          id={id}
          type="time"
          value={typeof valor === 'number' ? paraHora(valor) : ''}
          onChange={(e) => onMudar(e.target.value ? deHora(e.target.value) : null)}
        />
      ) : (
        <input
          className={campo.tipo === 'numero' ? 'input input--mono' : 'input'}
          id={id}
          type={campo.tipo === 'numero' ? 'number' : 'text'}
          inputMode={campo.tipo === 'numero' ? 'numeric' : undefined}
          value={texto}
          onChange={(e) => {
            const bruto = e.target.value
            if (bruto === '') return onMudar(null)
            onMudar(campo.tipo === 'numero' ? Number(bruto) : bruto)
          }}
        />
      )}

      {campo.tipo === 'referencia' && campo.referencia && opcoes.length === 0 && (
        // Dizer onde cadastrar é melhor que uma lista muda: numa escola
        // vazia, metade dos campos de referência começa assim.
        <span className="field__hint">
          Nada cadastrado ainda em {campo.referencia}. Comece por lá.
        </span>
      )}
      {campo.ajuda && <span className="field__hint">{campo.ajuda}</span>}
    </div>
  )
}
